// Paddle.js overlay checkout. Loads Paddle on demand, opens the $39 checkout,
// and reports the transaction id on success so the map can unlock instantly —
// the buyer never types or copies anything.

import { PADDLE } from './config.js'

let ready = null
let successCb = null
let errorCb = null

export function paddleConfigured() {
  return !!(PADDLE.token && PADDLE.priceId)
}

function loadPaddle() {
  if (ready) return ready
  ready = new Promise((resolve, reject) => {
    if (window.Paddle) return resolve(window.Paddle)
    const s = document.createElement('script')
    s.src = 'https://cdn.paddle.com/paddle/v2/paddle.js'
    s.async = true
    s.onload = () => {
      try {
        if (PADDLE.env !== 'production') window.Paddle.Environment.set('sandbox')
        window.Paddle.Initialize({ token: PADDLE.token, eventCallback: onPaddleEvent })
        resolve(window.Paddle)
      } catch (e) { reject(e) }
    }
    s.onerror = () => { ready = null; reject(new Error('paddle_load_failed')) }
    document.head.appendChild(s)
  })
  return ready
}

function onPaddleEvent(ev) {
  if (ev?.name === 'checkout.completed') {
    const txn = ev?.data?.transaction_id || ev?.data?.id
    if (txn && successCb) { const cb = successCb; successCb = null; cb(txn) }
  } else if (ev?.name === 'checkout.error' || /error/i.test(ev?.name || '')) {
    console.error('[paddle] error event', ev)
    const detail = ev?.error?.detail || ev?.detail || ev?.data?.message || ev?.name || 'unknown error'
    if (errorCb) errorCb(String(detail))
  }
}

// Open the overlay checkout. `onSuccess(transactionId)` fires once paid;
// `onError(message)` fires with Paddle's reason if the overlay fails.
export async function openCheckout(onSuccess, onError) {
  if (!paddleConfigured()) throw { code: 'not_configured' }
  successCb = onSuccess
  errorCb = onError
  const Paddle = await loadPaddle()
  try {
    Paddle.Checkout.open({ items: [{ priceId: PADDLE.priceId, quantity: 1 }] })
  } catch (e) {
    if (onError) onError(String(e?.message || e))
  }
}

// Dismiss the Paddle overlay once we've unlocked, so the buyer lands straight
// on the unlocked map (no manual close, no "what now?" thank-you screen).
export function closeCheckout() {
  try { window.Paddle?.Checkout?.close() } catch { /* overlay already gone */ }
}
